import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { RigidBody, CuboidCollider } from "@react-three/rapier";
import { Text, Html } from "@react-three/drei";
import * as THREE from "three";
import { Project } from "../../types";
import { getBarkTexture, getPlaceholderTexture } from "./textures";
import { useImageTextures } from "./imageLoader";
import LockIcon from "./LockIcon";
import NPCPerson from "./NPCPerson";
import CompoundFence from "./CompoundFence";
import { playerPosition } from "./controls";
import { playChatterBlip } from "./audio";

const CHATTER_RADIUS = 8;

interface ProjectMarkerProps {
  project: Project;
  position: [number, number, number];
  facingAngle: number;
  featured?: boolean;
  onEnter: (project: Project) => void;
  onExit: (project: Project) => void;
}

const DEFAULT_ASPECT = 0.47; // typical portrait phone screenshot

const ProjectMarker: React.FC<ProjectMarkerProps> = ({
  project,
  position,
  facingAngle,
  featured = false,
  onEnter,
  onExit,
}) => {
  const frameRef = useRef<THREE.Group>(null);
  const glowRef = useRef<THREE.PointLight>(null);
  const [hovered, setHovered] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const chatterTimer = useRef(1 + Math.random() * 2);
  const markerPos = useMemo(() => new THREE.Vector3(...position), [position]);

  const urls = project.screenshots.slice(0, featured ? 5 : 1);
  const { textures, aspect } = useImageTextures(urls);
  const bark = getBarkTexture();
  const placeholder = useMemo(
    () => getPlaceholderTexture(project.title, featured ? "#ffc94d" : "#8a8a82"),
    [project.title, featured],
  );

  // Cycle through screenshots for the featured project
  useEffect(() => {
    if (!featured || textures.length < 2) return;
    const id = setInterval(() => {
      setSlideIndex((i) => (i + 1) % textures.length);
    }, 2800);
    return () => clearInterval(id);
  }, [featured, textures.length]);

  useFrame(({ clock }, delta) => {
    if (frameRef.current) {
      frameRef.current.position.y =
        (featured ? 2.9 : 2.3) + Math.sin(clock.getElapsedTime() * 1.2 + position[0]) * 0.06;
    }
    if (glowRef.current) {
      glowRef.current.intensity = 1.4 + Math.sin(clock.getElapsedTime() * 2.4) * 0.5;
    }

    if (featured) {
      chatterTimer.current -= delta;
      if (chatterTimer.current <= 0) {
        chatterTimer.current = 1.2 + Math.random() * 1.8;
        if (playerPosition.distanceTo(markerPos) < CHATTER_RADIUS) {
          playChatterBlip();
        }
      }
    }
  });

  const frameHeight = featured ? 2.6 : 1.9;
  const frameWidth = frameHeight * (aspect ?? DEFAULT_ASPECT);
  const activeTexture = textures.length > 0 ? textures[slideIndex % textures.length] : placeholder;
  const postGap = frameWidth * 0.5 + 0.12;
  const postHeight = (featured ? 2.9 : 2.3) + frameHeight / 2;
  const accent = featured ? "#e8ab1f" : "#6b6b62";
  const postTint = featured ? "#ffffff" : "#8a8a82";

  return (
    <group position={position} rotation={[0, facingAngle, 0]}>
      {/* Sensor collider — driving through triggers the project detail */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider
          args={[Math.max(frameWidth, 1.4), 2, 1.4]}
          position={[0, 1.5, 0]}
          sensor
          onIntersectionEnter={() => onEnter(project)}
          onIntersectionExit={() => onExit(project)}
        />
      </RigidBody>

      {/* Base pad */}
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <cylinderGeometry args={[1.5, 1.5, 0.1, 6]} />
        <meshStandardMaterial color={hovered ? (featured ? "#c98f10" : "#4f4f48") : accent} roughness={0.7} />
      </mesh>

      {featured && (
        <pointLight ref={glowRef} position={[0, 2.9, 0.6]} color="#ffe8a3" intensity={1.6} distance={6} />
      )}

      {/* Support posts */}
      {[-postGap, postGap].map((x) => (
        <mesh key={x} position={[x, postHeight / 2, 0]} castShadow>
          <boxGeometry args={[0.14, postHeight, 0.14]} />
          <meshStandardMaterial map={bark} color={postTint} roughness={1} />
        </mesh>
      ))}

      {/* Picture frame */}
      <group
        ref={frameRef}
        position={[0, featured ? 2.9 : 2.3, 0]}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        onClick={() => onEnter(project)}
      >
        {/* backing border */}
        <mesh position={[0, 0, -0.04]}>
          <boxGeometry args={[frameWidth + 0.14, frameHeight + 0.14, 0.06]} />
          <meshStandardMaterial color={accent} roughness={0.6} />
        </mesh>
        {/* screenshot / placeholder */}
        <mesh position={[0, 0, 0]} castShadow>
          <planeGeometry args={[frameWidth, frameHeight]} />
          <meshStandardMaterial
            map={activeTexture}
            toneMapped={false}
            color={featured ? "#ffffff" : "#9a9a92"}
          />
        </mesh>

        {!featured && (
          <>
            {/* Dimming overlay — reads as an old, dead screen */}
            <mesh position={[0, 0, 0.01]}>
              <planeGeometry args={[frameWidth, frameHeight]} />
              <meshBasicMaterial color="#0a0a08" transparent opacity={0.45} />
            </mesh>
            <LockIcon position={[0, 0, 0.06]} scale={1.4} />
          </>
        )}

        {featured && (
          <Html
            position={[0, frameHeight / 2 + 0.35, 0]}
            center
            distanceFactor={8}
            occlude={false}
            style={{ pointerEvents: "auto" }}
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                whiteSpace: "nowrap",
                fontFamily: "monospace",
              }}
            >
              <span
                style={{
                  background: "#22c55e",
                  color: "white",
                  fontWeight: 700,
                  fontSize: 12,
                  padding: "3px 8px",
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  animation: "worldLivePulse 1.6s ease-in-out infinite",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "white",
                  }}
                />
                LIVE
              </span>
              <span
                style={{
                  background: "#ffc94d",
                  color: "#212121",
                  fontWeight: 700,
                  fontSize: 12,
                  padding: "3px 8px",
                  borderRadius: 999,
                  border: "1px solid #21212122",
                }}
              >
                ★ Flagship App
              </span>
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#212121",
                  color: "white",
                  fontWeight: 600,
                  fontSize: 12,
                  padding: "3px 8px",
                  borderRadius: 999,
                  textDecoration: "none",
                }}
              >
                ▶ Google Play
              </a>
              <style>{`
                @keyframes worldLivePulse {
                  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
                  50% { opacity: 0.85; box-shadow: 0 0 0 4px rgba(34,197,94,0); }
                }
              `}</style>
            </div>
          </Html>
        )}
      </group>

      <Text
        position={[0, featured ? 1.35 : 1.15, 0.05]}
        fontSize={featured ? 0.24 : 0.19}
        color={featured ? "#212121" : "#6b6b62"}
        anchorX="center"
        anchorY="middle"
        maxWidth={3.2}
        textAlign="center"
        outlineWidth={0.01}
        outlineColor="#ffffff"
      >
        {project.title}
      </Text>

      {!featured && (
        <Text
          position={[0, featured ? 1.0 : 0.82, 0.05]}
          fontSize={0.13}
          color="#8a4a3a"
          anchorX="center"
          anchorY="middle"
          maxWidth={3.2}
          textAlign="center"
        >
          🔒 Archived — no longer maintained
        </Text>
      )}

      {/* A small compound fence around the flagship app, with a gate on the approach side */}
      {featured && <CompoundFence radius={3.4} />}

      {/* Visitors patrolling within the compound — feels alive/active */}
      {featured && (
        <>
          <NPCPerson center={[1.6, 0, 1.4]} radius={1.1} speed={0.35} phase={0} shirtTint="#3b82c4" />
          <NPCPerson center={[-1.8, 0, 1.2]} radius={0.9} speed={0.28} phase={2.4} shirtTint="#c45a3b" />
          <NPCPerson center={[0.3, 0, -1.8]} radius={1.0} speed={0.32} phase={1.1} shirtTint="#8a4fc4" />
          <NPCPerson center={[-1.0, 0, -1.5]} radius={0.8} speed={0.4} phase={3.6} shirtTint="#2fa89a" />
          <NPCPerson center={[2.0, 0, -0.5]} radius={0.9} speed={0.26} phase={4.8} shirtTint="#c4a02f" />
        </>
      )}
    </group>
  );
};

export default ProjectMarker;
